import type { AnimationRule } from './animations'; // Import type
import {
	clearDynamicStyles,
	generateSlideAnimationCss,
	injectDynamicStyles,
} from './animations';

export interface SmartViewTransitionContext {
	scope?: string;
	group?: string;
}

export interface StartSmartViewTransitionOptions {
	trigger: () => void | Promise<void>;
	context?: SmartViewTransitionContext;
	shouldParticipate?: (
		node: HTMLElement,
		context: SmartViewTransitionContext,
	) => boolean;
	customAnimations?: AnimationRule | AnimationRule[];
}

function getParticipatingNodes(
	context: SmartViewTransitionContext = {},
	shouldParticipate?: (
		node: HTMLElement,
		context: SmartViewTransitionContext,
	) => boolean,
): HTMLElement[] {
	const participatingNodes: HTMLElement[] = [];
	const effectiveScope = context.scope || 'in-page';

	// --- Group-based participation logic ---
	// Precedence is given to group roots that are explicitly marked for transition.
	// This helps avoid nested view-transition-name assignments, where a parent and child
	// both try to animate independently in the same transition, which is not supported.
	if (context.group) {
		// 1. Check for group roots that also have an explicit `data-vt` attribute.
		// These are primary candidates for transitioning the entire group.
		const groupRoots = Array.from(
			document.querySelectorAll(`[data-vt-group="${context.group}"][data-vt]`),
		) as HTMLElement[];

		let groupRootParticipated = false;
		for (const root of groupRoots) {
			const vtName = root.getAttribute('data-vt'); // `data-vt` is mandatory for participation.
			if (!vtName) continue;

			const nodeScope = root.getAttribute('data-vt-scope') || 'in-page';
			if (nodeScope !== effectiveScope) continue;

			const currentContext = { ...context, scope: effectiveScope };
			if (shouldParticipate && !shouldParticipate(root, currentContext))
				continue;

			root.style.viewTransitionName = vtName;
			participatingNodes.push(root);
			groupRootParticipated = true;
		}

		// If a group root (with `data-vt`) was identified and participated,
		// we *do not* look for its children to participate in this same transition.
		// This is key to preventing nested transition conflicts.
		if (groupRootParticipated) {
			return participatingNodes;
		}

		// 2. If no group root (with `data-vt`) participated, then (and only then)
		//    look for descendant elements within the group that have their own `data-vt`.
		//    These children can then participate individually if the group root isn't.
		const childNodesInGroup = Array.from(
			document.querySelectorAll(`[data-vt-group="${context.group}"] [data-vt]`),
		) as HTMLElement[];

		childNodesInGroup.forEach((node) => {
			// Ensure this node is not one of the groupRoots already processed (and skipped if it lacked data-vt initially but was a groupRoot candidate)
			if (groupRoots.includes(node)) return;

			const vtName = node.getAttribute('data-vt'); // `data-vt` is mandatory.
			if (!vtName) return;

			const nodeScope = node.getAttribute('data-vt-scope') || 'in-page';
			if (nodeScope !== effectiveScope) return;

			const currentContext = { ...context, scope: effectiveScope };
			if (shouldParticipate && !shouldParticipate(node, currentContext)) return;

			node.style.viewTransitionName = vtName;
			participatingNodes.push(node);
		});
	} else {
		// --- Non-group participation logic ---
		// No group context: find all elements globally that have `data-vt` and match the scope.
		const allNodesWithVt = Array.from(
			document.querySelectorAll('[data-vt]'),
		) as HTMLElement[];
		allNodesWithVt.forEach((node) => {
			const vtName = node.getAttribute('data-vt'); // `data-vt` is mandatory.
			if (!vtName) return; // Should not happen with [data-vt] selector, but good practice

			const nodeScope = node.getAttribute('data-vt-scope') || 'in-page';
			if (nodeScope !== effectiveScope) return;

			const currentContext = { ...context, scope: effectiveScope };
			if (shouldParticipate && !shouldParticipate(node, currentContext)) return;

			node.style.viewTransitionName = vtName;
			participatingNodes.push(node);
		});
	}

	// Remove duplicates that might arise from complex query scenarios (though less likely with new logic)
	return Array.from(new Set(participatingNodes));
}

/**
 * Initiates a smart view transition with granular control over participating elements.
 * Uses data attributes (`data-vt`, `data-vt-scope`, `data-vt-group`) to determine participation.
 *
 * @remarks
 * ### Element Participation Rules:
 * 1.  **`data-vt` is Mandatory**: An element *must* have an explicit `data-vt="unique-name"` attribute to be
 *     considered for a view transition and to receive a `view-transition-name` style.
 * 2.  **Scope**: `data-vt-scope` (defaults to "in-page") on an element must match the `context.scope`
 *     (defaults to "in-page") for the element to be considered.
 * 3.  **Group Precedence (Important Limitation)**:
 *     - If `context.group` is provided, the function first looks for elements with
 *       `[data-vt-group="groupName"][data-vt]` (i.e., group roots that are explicitly marked for transition).
 *     - If such a group root participates, **none of its descendant elements will participate in this specific
 *       transition call**, even if they also have `data-vt`. This prevents unsupported nested
 *       `view-transition-name` assignments.
 *     - If no group root (with `data-vt`) participates, *then* descendants within that group that have their
 *       own `data-vt` attribute will be considered.
 * 4.  **No Group**: If `context.group` is NOT provided, all elements in the document with a `data-vt` attribute
 *     matching the `context.scope` are considered.
 *
 * This function only proceeds if `document.startViewTransition` is supported by the browser;
 * otherwise, it directly calls the `trigger` function.
 *
 * @example
 * ```html
 * <!-- Scenario 1: Transitioning a specific content area -->
 * <div data-vt="dialog-content" data-vt-scope="dialog">...</div>
 *
 * <!-- Scenario 2: Transitioning a whole widget (group root) -->
 * <div data-vt-group="profile-widget" data-vt="profile-transition-name">
 *   <h2 data-vt="profile-header">Profile</h2> <!-- This will NOT participate if profile-widget does -->
 *   <p>Details...</p>
 * </div>
 *
 * <!-- Scenario 3: Transitioning children if group root doesn't have data-vt -->
 * <div data-vt-group="article-section">
 *   <h3 data-vt="article-title">Title</h3>
 *   <p data-vt="article-summary">Summary</p>
 * </div>
 * ```
 * ```javascript
 * // Example 1: Slide a specific dialog content area
 * startSmartViewTransition({
 *   trigger: () => {
 *     // Update DOM to show new dialog content
 *   },
 *   context: { scope: 'dialog' }, // Matches data-vt-scope
 *   customAnimations: [
 *     { viewTransitionName: 'dialog-content', animationType: 'slide', direction: 'left' }
 *   ]
 * });
 *
 * // Example 2: Transition the entire profile widget
 * startSmartViewTransition({
 *   trigger: () => {
 *     // Update profile widget content
 *   },
 *   context: { group: 'profile-widget' }, // Group context
 *   customAnimations: [
 *      // Animation rule can use context.group if viewTransitionName is omitted
 *     { animationType: 'slide', direction: 'up' }
 *   ]
 * });
 * // The div with data-vt-group="profile-widget" and data-vt="profile-transition-name" will transition.
 * // Its child <h2 data-vt="profile-header"> will NOT participate.
 *
 * // Example 3: Transition article title and summary individually (if article-section has no data-vt)
 * startSmartViewTransition({
 *   trigger: () => {
 *      // Update article content
 *   },
 *   context: { group: 'article-section' },
 *   customAnimations: [
 *     { viewTransitionName: 'article-title', animationType: 'slide', direction: 'left' },
 *     { viewTransitionName: 'article-summary', animationType: 'slide', direction: 'left', duration: '0.5s' }
 *   ]
 * });
 * ```
 *
 * @param options - Configuration for the view transition.
 * @param options.trigger - A callback function that performs the DOM update that should be transitioned.
 *                        Can be synchronous or asynchronous (return a Promise).
 * @param options.context - Optional context to scope element participation.
 * @param options.context.scope - Elements must have a matching `data-vt-scope` (or default "in-page").
 * @param options.context.group - Specifies a group to consider for transitions. Influences node selection strategy.
 * @param options.shouldParticipate - Optional callback to further filter which discovered elements should participate.
 *                                    Receives the candidate HTMLElement and the current SmartViewTransitionContext.
 *                                    Return `true` to include the node, `false` to exclude.
 * @param options.customAnimations - Optional. A single {@link AnimationRule} or an array of them to apply
 *                                   custom animations (e.g., slides) to specific view transition names.
 */
export function startSmartViewTransition({
	trigger,
	context = {},
	shouldParticipate,
	customAnimations,
}: StartSmartViewTransitionOptions): void {
	if (document.startViewTransition) {
		const participatingNodes = getParticipatingNodes(
			context,
			shouldParticipate,
		);

		const effectiveCustomAnimations: AnimationRule[] = customAnimations
			? Array.isArray(customAnimations)
				? customAnimations
				: [customAnimations]
			: [];

		let dynamicCss = '';
		effectiveCustomAnimations.forEach((rule) => {
			const targetViewTransitionName = rule.viewTransitionName || context.group;
			if (!targetViewTransitionName) {
				console.warn(
					'Custom animation rule skipped: missing viewTransitionName and no context.group.',
					rule,
				);
				return;
			}
			const effectiveRule = {
				...rule,
				viewTransitionName: targetViewTransitionName,
			};
			if (effectiveRule.animationType === 'slide') {
				dynamicCss += generateSlideAnimationCss(effectiveRule);
			}
		});

		if (dynamicCss) {
			injectDynamicStyles(dynamicCss);
		}

		document.startViewTransition(trigger).finished.finally(() => {
			participatingNodes.forEach((node) => {
				node.style.viewTransitionName = '';
			});
			if (dynamicCss) {
				clearDynamicStyles();
			}
		});
	} else {
		trigger();
	}
}
